import { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Segmented,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Radio,
  Typography,
  Tag,
  Empty,
  Skeleton,
  Space,
  Popconfirm,
  Checkbox,
  Dropdown,
  message,
  Select,
} from "antd";
import type { MenuProps } from "antd";
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
  LoadingOutlined,
  DownOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { T_CreateManualEntryRequest, T_MergedTransaction } from "dtos/loan-transaction.dto";
import { ApiService } from "api/api.service";
import { formatMoney, getErrorMessage } from "utils/functions";
import { T_Populate } from "types/_utils.types";
import { LoanSchemaDto } from "dtos/loan.dto";
import { FileSchemaDto } from "dtos/file.dto";
import { BankSchemaDto } from "dtos/bank.dto";
import { LoanPaymentInsightsModal } from "components/Application/LoanPaymentInsights";

const { Text, Title } = Typography;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  appKey: string;
  selectedLoan:
    | T_Populate<
        LoanSchemaDto,
        {
          attachments: FileSchemaDto[];
        }
      >
    | undefined;
  open: boolean;
  banks: T_Populate<
    BankSchemaDto,
    {
      attachments: FileSchemaDto[];
    }
  >[];
  onClose: () => void;
};

type T_ManualModalState = { mode: "closed" } | { mode: "add" } | { mode: "edit"; entry: T_MergedTransaction };

type T_ManualEntryFormValues = {
  account_number: string;
  transaction_date: Dayjs;
  description: string;
  direction: "debit" | "credit";
  amount: number;
};

const COLOR_PENDING = "#F59E0B";
const COLOR_ACCEPTED = "#22C55E";
const COLOR_DEBIT = "#4b5563";

export function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

export function formatMonthLabel(iso: string): string {
  return dayjs(iso).format("MMMM YYYY").toUpperCase();
}

function formatCardDate(iso: string): string {
  return dayjs(iso).format("ddd, MMM D");
}

type T_BulkAction = "accept-selected" | "reject-selected" | "accept-except-selected" | "reject-except-selected";

const BULK_ACTIONS: { key: T_BulkAction; label: string }[] = [
  { key: "accept-selected", label: "Accept selected only" },
  { key: "reject-selected", label: "Reject selected only" },
  { key: "accept-except-selected", label: "Accept all except selected" },
  { key: "reject-except-selected", label: "Reject all except selected" },
];

function ManualEntryModal({
  state,
  onCancel,
  onSubmit,
  submitting,
  accountNumbers,
}: {
  state: T_ManualModalState;
  onCancel: () => void;
  onSubmit: (body: T_CreateManualEntryRequest) => void;
  submitting: boolean;
  accountNumbers: string[];
}) {
  const [form] = Form.useForm<T_ManualEntryFormValues>();
  const isEdit = state.mode === "edit";

  useEffect(() => {
    if (state.mode === "edit") {
      const isCredit = state.entry.amount >= 0;
      form.setFieldsValue({
        account_number: state.entry.account_number,
        transaction_date: dayjs(state.entry.transaction_date),
        description: state.entry.description,
        direction: isCredit ? "credit" : "debit",
        amount: Math.abs(state.entry.amount),
      });
    } else if (state.mode === "add") {
      form.resetFields();
    }
  }, [state, form]);

  function handleFinish(values: T_ManualEntryFormValues) {
    const magnitude = Math.abs(values.amount);
    onSubmit({
      account_number: values.account_number,
      transaction_date: values.transaction_date.format("YYYY-MM-DD"),
      description: values.description,
      amount: -magnitude,
    });
  }

  return (
    <Modal
      title={isEdit ? "Edit transaction" : "Add transaction"}
      open={state.mode !== "closed"}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" initialValues={{ direction: "debit" }} onFinish={handleFinish}>
        <Form.Item
          name="account_number"
          label="Account number"
          // rules={[{ required: true, message: "Account number is required" }]}
        >
          {/* <Input placeholder="xxxx4235" /> */}
          <Select
            placeholder="xxxx4235"
            className="!w-full"
            options={accountNumbers.map((acc) => ({
              label: acc,
              value: acc,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          // rules={[{ required: true, message: "Description is required" }]}
        >
          <Input placeholder="e.g. Wells Fargo Auto Pmt" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-3">
          <Form.Item
            name="transaction_date"
            label="Transaction date"
            rules={[{ required: true, message: "Transaction date is required" }]}
          >
            <DatePicker className="w-full" format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: "Amount is required" }]}>
            <InputNumber
              className="w-full"
              min={0}
              precision={2}
              formatter={(value) => (value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "")}
              parser={(value) => (value ? (value.replace(/\$\s?|(,*)/g, "") as unknown as number) : (0 as any))}
            />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 mt-1">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {isEdit ? "Save changes" : "Add transaction"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

function TransactionRowSkeleton() {
  return (
    <div className="rounded-lg pl-3 pr-4 py-2.5 flex items-center justify-between gap-4 bg-white border border-solid border-zinc-200 animate-pulse">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="h-2.5 w-16 rounded bg-zinc-200" />
          <div className="h-2.5 w-12 rounded bg-zinc-100" />
        </div>
        <div className="h-3 w-48 max-w-full rounded bg-zinc-200" />
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="h-3 w-14 rounded bg-zinc-200" />
        <div className="h-7 w-20 rounded-md bg-zinc-100" />
      </div>
    </div>
  );
}

function MonthGroupSkeleton({ rows }: { rows: number }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2 px-0.5 animate-pulse">
        <div className="h-2.5 w-24 rounded bg-zinc-200" />
        <div className="h-3 w-16 rounded bg-zinc-200" />
      </div>
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: rows }).map((_, i) => (
          <TransactionRowSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

function TransactionListSkeleton() {
  return (
    <div className="flex flex-col gap-6 px-4">
      <MonthGroupSkeleton rows={3} />
      <MonthGroupSkeleton rows={2} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error state
// ---------------------------------------------------------------------------

function TransactionErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      <div
        className="flex items-center justify-center rounded-full mb-4"
        style={{ width: 48, height: 48, background: "#FEF2F2" }}
      >
        <WarningOutlined style={{ fontSize: 22, color: COLOR_DEBIT }} />
      </div>
      <Text style={{ fontSize: 14, fontWeight: 600, color: "#18181B" }}>{`Couldn't load transactions`}</Text>
      <Text type="secondary" style={{ fontSize: 12, maxWidth: 320, marginTop: 4 }}>
        {message}
      </Text>
      <Button className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function TransactionProcessingState() {
  return (
    <div className="flex flex-col gap-6 px-4">
      <div
        className="flex items-center gap-2 rounded-lg px-3 py-2.5"
        style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
      >
        <LoadingOutlined style={{ fontSize: 14, color: "#2563EB" }} />
        <Text style={{ fontSize: 12, color: "#1D4ED8" }}>
          {`We're still processing your bank statements — this can take a few minutes.`}
        </Text>
      </div>
      <MonthGroupSkeleton rows={3} />
      <MonthGroupSkeleton rows={2} />
    </div>
  );
}

function TransactionRow({
  tx,
  variant,
  onConfirm,
  onDiscard,
  onEdit,
  onDelete,
  actionLoading,
  selectable,
  selected,
  onSelectChange,
}: {
  tx: T_MergedTransaction;
  variant: "pending" | "accepted";
  onConfirm?: (id: string) => void;
  onDiscard?: (id: string) => void;
  onEdit?: (tx: T_MergedTransaction) => void;
  onDelete?: (tx: T_MergedTransaction) => void;
  actionLoading?: boolean;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (id: string, checked: boolean) => void;
}) {
  const isCredit = tx.amount >= 0;

  return (
    <div
      className="rounded-lg pl-3 pr-4 py-2.5 flex items-center gap-3 bg-white border border-solid border-zinc-300"
      // style={{ borderLeft: `3px solid ${borderColor}` }}
    >
      {selectable && <Checkbox checked={selected} onChange={(e) => onSelectChange?.(tx.id, e.target.checked)} />}

      <div className="min-w-0 flex-1 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <Text style={{ fontSize: 11, color: "#71717A" }}>{formatCardDate(tx.transaction_date)}</Text>
            <Text style={{ fontSize: 11, color: "#A1A1AA" }}>·</Text>
            {tx.account_number?.toLocaleLowerCase() === "unknown" ? null : (
              <Text
                style={{
                  fontSize: 11,

                  color: "#3F3F46",
                  background: "#F4F4F5",
                  padding: "1px 6px",
                  borderRadius: 4,
                }}
              >
                {tx.account_number}
              </Text>
            )}
            {tx?.source === "manual" && (
              <Tag color="purple" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>
                Manual
              </Tag>
            )}
          </div>
          {tx.description && (
            <Text className="block truncate" style={{ fontSize: 13, color: "#18181B" }} title={tx.description}>
              {tx.description}
            </Text>
          )}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <Text style={{ fontSize: 13, fontWeight: 600, color: isCredit ? COLOR_ACCEPTED : COLOR_DEBIT }}>
            {formatMoney(Math.abs(tx.amount))}
          </Text>

          {variant === "pending" && (
            <Space size={6}>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                style={{ background: COLOR_ACCEPTED, borderColor: COLOR_ACCEPTED }}
                loading={actionLoading}
                onClick={() => onConfirm?.(tx.id)}
              >
                Confirm
              </Button>
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                loading={actionLoading}
                onClick={() => onDiscard?.(tx.id)}
              ></Button>
            </Space>
          )}

          {variant === "accepted" && tx.source === "manual" && (
            <Space size={6}>
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit?.(tx)} />
              <Popconfirm
                title="Delete this transaction?"
                description="This cannot be undone."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => onDelete?.(tx)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )}
        </div>
      </div>
    </div>
  );
}

function MonthGroupedList({
  items,
  variant,
  onConfirm,
  onDiscard,
  onEdit,
  onDelete,
  pendingActionId,
  emptyText,
  selectable,
  selectedIds,
  onToggleSelect,
  onToggleMonth,
}: {
  items: T_MergedTransaction[];
  variant: "pending" | "accepted";
  onConfirm?: (id: string) => void;
  onDiscard?: (id: string) => void;
  onEdit?: (tx: T_MergedTransaction) => void;
  onDelete?: (tx: T_MergedTransaction) => void;
  pendingActionId?: string | null;
  emptyText: string;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string, checked: boolean) => void;
  onToggleMonth?: (ids: string[], checked: boolean) => void;
}) {
  const groups = useMemo(() => {
    const map = new Map<string, T_MergedTransaction[]>();
    for (const t of items) {
      const key = monthKey(t.transaction_date);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(t);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, groupItems]) => ({
        key,
        label: formatMonthLabel(groupItems[0].transaction_date),
        items: [...groupItems].sort((a, b) => a.transaction_date.localeCompare(b.transaction_date)),
        total: groupItems.reduce((s, i) => s + i.amount, 0),
      }));
  }, [items]);

  if (items.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} className="py-10" />;
  }

  return (
    <div className="flex flex-col gap-6 px-4 pb-4">
      {groups.map((group) => {
        const groupIds = group.items.map((tx) => tx.id);
        const selectedCount = selectable ? groupIds.filter((id) => selectedIds?.has(id)).length : 0;
        const allSelected = selectable && selectedCount > 0 && selectedCount === groupIds.length;
        const someSelected = selectable && selectedCount > 0 && selectedCount < groupIds.length;

        return (
          <div key={group.key}>
            <div className="flex items-baseline justify-between mb-2 px-0.5">
              <div className="flex items-center gap-2">
                {selectable && (
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => onToggleMonth?.(groupIds, e.target.checked)}
                  />
                )}
                <Text style={{ fontSize: 11, letterSpacing: "0.09em", color: "#A1A1AA", fontWeight: 500 }}>
                  {group.label}
                </Text>
              </div>
              <Text style={{ fontSize: 13, fontWeight: 600, color: group.total < 0 ? COLOR_DEBIT : COLOR_ACCEPTED }}>
                {formatMoney(Math.abs(group.total))}
              </Text>
            </div>
            <div className="flex flex-col gap-1.5">
              {group.items.map((tx) => (
                <TransactionRow
                  key={tx.id}
                  tx={tx}
                  variant={variant}
                  onConfirm={onConfirm}
                  onDiscard={onDiscard}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  actionLoading={pendingActionId === tx.id}
                  selectable={selectable}
                  selected={selectedIds?.has(tx.id)}
                  onSelectChange={onToggleSelect}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LoanReconciliationDrawer({ appKey, selectedLoan, open, onClose, banks }: Props) {
  const [activeTab, setActiveTab] = useState<"pending" | "accepted">("pending");
  const [modalState, setModalState] = useState<T_ManualModalState>({ mode: "closed" });
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isInsightsOpen, setIsInsightsOpen] = useState(false);

  const pendingQuery = ApiService.application.usePendingTransactions({
    appKey,
    loanId: selectedLoan?._id as string,
    enabled: open,
  });
  const acceptedQuery = ApiService.application.useAcceptedTransactions({
    appKey,
    loanId: selectedLoan?._id as string,
    enabled: open,
  });

  const acceptMutation = ApiService.application.useAcceptTransaction();
  const rejectMutation = ApiService.application.useRejectTransaction();
  const addManualMutation = ApiService.application.useAddManualEntry();
  const updateManualMutation = ApiService.application.useUpdateManualEntry();
  const deleteManualMutation = ApiService.application.useDeleteManualEntry();
  const bulkUpdateMutation = ApiService.application.useBulkUpdateTransactions();

  const pendingItems = pendingQuery.data?.result.transactions ?? [];
  const acceptedItems = acceptedQuery.data?.result.transactions ?? [];
  const isPendingProcessing = pendingQuery.data?.result.status === "processing";
  const isAcceptedProcessing = acceptedQuery.data?.result.status === "processing";

  const acceptedTotal = useMemo(() => acceptedItems?.reduce((sum, t) => sum + t.amount, 0), [acceptedItems]);

  // Selection only applies to the pending tab — drop it when leaving pending, or
  // when the pending list changes underneath it (e.g. after a bulk/single action).
  useEffect(() => {
    if (activeTab !== "pending") {
      if (selectedIds.size > 0) setSelectedIds(new Set());
      return;
    }
    const validIds = new Set(pendingItems.map((t) => t.id));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pendingItems]);

  function handleToggleSelect(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function handleToggleMonth(ids: string[], checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  }

  function handleSelectAll(checked: boolean) {
    setSelectedIds(checked ? new Set(pendingItems.map((t) => t.id)) : new Set());
  }

  function handleBulkAction(action: T_BulkAction) {
    const allIds = pendingItems.map((t) => t.id);
    const selected = Array.from(selectedIds);
    const unselected = allIds.filter((id) => !selectedIds.has(id));

    let accepted_ids: string[] = [];
    let rejected_ids: string[] = [];
    let title = "";
    let description = "";

    switch (action) {
      case "accept-selected":
        accepted_ids = selected;
        title = `Accept ${selected.length} selected transaction${selected.length === 1 ? "" : "s"}?`;
        description = "These transactions will be moved to Accepted.";
        break;
      case "reject-selected":
        rejected_ids = selected;
        title = `Reject ${selected.length} selected transaction${selected.length === 1 ? "" : "s"}?`;
        description = "These transactions will be removed from the pending list.";
        break;
      case "accept-except-selected":
        accepted_ids = unselected;
        title = `Accept all except ${selected.length} selected?`;
        description = `${unselected.length} transaction${unselected.length === 1 ? "" : "s"} will be moved to Accepted.`;
        break;
      case "reject-except-selected":
        rejected_ids = unselected;
        title = `Reject all except ${selected.length} selected?`;
        description = `${unselected.length} transaction${unselected.length === 1 ? "" : "s"} will be removed from the pending list.`;
        break;
    }

    Modal.confirm({
      title,
      content: description,
      okText: "Confirm",
      onOk: () => {
        bulkUpdateMutation.mutate(
          { appKey, loanId: selectedLoan?._id as string, body: { accepted_ids, rejected_ids } },
          {
            onSuccess: () => {
              message.success("Transactions updated");
              setSelectedIds(new Set());
            },
            onError: () => message.error("Couldn't update these transactions — try again"),
          },
        );
      },
    });
  }

  const bulkActionMenu: MenuProps = {
    items: BULK_ACTIONS.map((a) => ({ key: a.key, label: a.label })),
    onClick: ({ key }) => handleBulkAction(key as T_BulkAction),
  };

  function handleConfirm(transactionId: string) {
    setPendingActionId(transactionId);
    acceptMutation.mutate(
      { appKey, loanId: selectedLoan?._id as string, transactionId },
      {
        onSuccess: () => {
          message.success("Transaction accepted");
        },
        onError: () => message.error("Couldn't accept this transaction — try again"),
        onSettled: () => setPendingActionId(null),
      },
    );
  }

  function handleDiscard(transactionId: string) {
    setPendingActionId(transactionId);
    rejectMutation.mutate(
      { appKey, loanId: selectedLoan?._id as string, transactionId },
      {
        onSuccess: () => message.success("Transaction rejected"),
        onError: () => message.error("Couldn't reject this transaction — try again"),
        onSettled: () => setPendingActionId(null),
      },
    );
  }

  function handleManualSubmit(body: T_CreateManualEntryRequest) {
    if (modalState.mode === "edit") {
      updateManualMutation.mutate(
        { appKey, loanId: selectedLoan?._id as string, entryId: modalState.entry.id, body },
        {
          onSuccess: () => {
            message.success("Transaction updated");
            setModalState({ mode: "closed" });
          },
          onError: () => message.error("Couldn't update this transaction — try again"),
        },
      );
    } else {
      addManualMutation.mutate(
        { appKey, loanId: selectedLoan?._id as string, body },
        {
          onSuccess: () => {
            message.success("Transaction added");
            setModalState({ mode: "closed" });
            setActiveTab("accepted");
          },
          onError: () => message.error("Couldn't add this transaction — try again"),
        },
      );
    }
  }

  function handleDeleteManual(tx: T_MergedTransaction) {
    deleteManualMutation.mutate(
      { appKey, loanId: selectedLoan?._id as string, entryId: tx.id },
      {
        onSuccess: () => message.success("Transaction deleted"),
        onError: () => message.error("Couldn't delete this transaction — try again"),
      },
    );
  }

  return (
    <>
      <Drawer
        title={
          <div className="flex flex-col items-start">
            <Title className="!text-sm" style={{ margin: 0 }}>
              Loan Payment History
            </Title>
            {selectedLoan?.creditor_name && (
              <Text className="!text-xs !text-gray-600">{selectedLoan?.creditor_name}</Text>
            )}
          </div>
        }
        open={open}
        onClose={onClose}
        width={640}
        styles={{ body: { background: "#fff" } }}
        extra={
          <Space>
            {acceptedItems?.length > 0 && (
              <Button icon={<LineChartOutlined />} onClick={() => setIsInsightsOpen(true)}>
                Insights
              </Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ mode: "add" })}>
              Add transaction
            </Button>
          </Space>
        }
      >
        {pendingQuery.isError || acceptedQuery.isError ? (
          <TransactionErrorState
            message={getErrorMessage(pendingQuery.error || acceptedQuery.error)}
            onRetry={() => {
              pendingQuery.refetch();
              acceptedQuery.refetch();
            }}
          />
        ) : (
          <>
            <div className="flex items-center justify-between px-4 my-4">
              <Segmented
                value={activeTab}
                onChange={(value) => setActiveTab(value as "pending" | "accepted")}
                options={[
                  { label: `Pending (${pendingItems.length})`, value: "pending" },
                  { label: `Verified (${acceptedItems.length})`, value: "accepted" },
                ]}
              />
              <div className="text-right">
                <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                  Verified total
                </Text>
                <Text
                  style={{ fontSize: 13, fontWeight: 600, color: acceptedTotal < 0 ? COLOR_DEBIT : COLOR_ACCEPTED }}
                >
                  {formatMoney(Math.abs(acceptedTotal))}
                </Text>
              </div>
            </div>

            {activeTab === "pending" ? (
              pendingQuery.isLoading ? (
                <TransactionListSkeleton />
              ) : isPendingProcessing ? (
                <TransactionProcessingState />
              ) : (
                <>
                  {pendingItems.length > 0 && (
                    <div className="flex items-center justify-between px-4 pb-3">
                      <Checkbox
                        checked={selectedIds.size > 0 && selectedIds.size === pendingItems.length}
                        indeterminate={selectedIds.size > 0 && selectedIds.size < pendingItems.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      >
                        Select all
                      </Checkbox>

                      {selectedIds.size > 0 && (
                        <Dropdown menu={bulkActionMenu} trigger={["click"]}>
                          <Button loading={bulkUpdateMutation.isPending}>
                            {selectedIds.size} selected <DownOutlined />
                          </Button>
                        </Dropdown>
                      )}
                    </div>
                  )}
                  <MonthGroupedList
                    items={pendingItems}
                    variant="pending"
                    onConfirm={handleConfirm}
                    onDiscard={handleDiscard}
                    pendingActionId={pendingActionId}
                    emptyText="No pending transactions"
                    selectable
                    selectedIds={selectedIds}
                    onToggleSelect={handleToggleSelect}
                    onToggleMonth={handleToggleMonth}
                  />
                </>
              )
            ) : acceptedQuery.isLoading ? (
              <TransactionListSkeleton />
            ) : isAcceptedProcessing ? (
              <TransactionProcessingState />
            ) : (
              <MonthGroupedList
                items={acceptedItems}
                variant="accepted"
                onEdit={(tx) => setModalState({ mode: "edit", entry: tx })}
                onDelete={handleDeleteManual}
                emptyText="No accepted transactions yet"
              />
            )}
          </>
        )}
      </Drawer>

      <ManualEntryModal
        state={modalState}
        onCancel={() => setModalState({ mode: "closed" })}
        onSubmit={handleManualSubmit}
        submitting={addManualMutation.isPending || updateManualMutation.isPending}
        accountNumbers={banks.map((b) => b.account_number ?? "")}
      />

      <LoanPaymentInsightsModal
        open={isInsightsOpen}
        onClose={() => setIsInsightsOpen(false)}
        loan={selectedLoan}
        acceptedItems={acceptedItems}
        pendingItems={pendingItems}
      />
    </>
  );
}
